<template>
  <div :class="['aggregate', (useStatusType || useType) && isBetween && 'break']">
    <CheckBox v-if="useStatusType" :value="1" v-model="statusType" small class="status">来店済みのみ</CheckBox>
    <SelectBox v-if="useTimeZone" :items="timeZones" v-model.number="timeZone" class="time-zone" />
    <SelectBox v-if="useType" :items="types" v-model.number="type" class="route" />
    <div class="terms">
      <template v-if="isBetween">
        <YearMonthInput v-model="startTerm" :yearMonths="betweenDates" :scrollTo="10" class="start" />
        <span>〜</span>
      </template>
      <YearMonthInput v-model="endTerm" :yearMonths="betweenDates" :scrollTo="10" class="end" />
    </div>
    <Button @click="search" primary small class="search" :disabled="busy || changed">表示</Button>
    <p v-if="isBetween" class="remarks">
      ※期間設定は最大24ヶ月
    </p>
  </div>
</template>

<script>
import { models } from '@/utils/vue'
import { mapState, mapGetters } from 'vuex'
import { addMonths, subMonths, format, differenceInMonths } from 'date-fns'
import { isEqual } from 'lodash'
import Button from '@/components/atoms/Button'
import CheckBox from '@/components/atoms/CheckBox'
import SelectBox from '@/components/atoms/SelectBox'
import YearMonthInput from '@/components/molecules/YearMonthInput'
import aggregateMixin from './mixin'

export { default as Unit } from './Unit'

const yearMonthToDate = term => {
  const [year, month] = term.split('-')
  return new Date(year, month - 1, 1)
}

export default {
  name: 'AnalysisAggregate',
  components: {
    Button,
    CheckBox,
    SelectBox,
    YearMonthInput,
  },
  mixins: [aggregateMixin],
  props: {
    busy: {
      type: Boolean,
      required: true,
    },
    refetch: {
      type: Function,
      required: true,
    },
    useStatusType: {
      type: Boolean,
      required: false,
      default: false,
    },
    useTimeZone: {
      type: Boolean,
      required: false,
      default: false,
    },
    useType: {
      type: Boolean,
      required: false,
      default: false,
    },
    defaultUnit: {
      type: Number,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      beforeConditions: {},
    }
  },
  computed: {
    ...mapState({
      conditions: state => state.analysis.aggregate.conditions,
      betweenDates: state => state.analysis.aggregate.betweenDates,
    }),
    ...mapGetters({
      isBetween: 'analysis/aggregate/isBetween',
    }),
    ...models(
      ['timeZone', 'type'],
      ({ conditions }, key) => conditions[key],
      ({ $store }, key, value) => $store.commit('analysis/aggregate/update', { [key]: value }),
    ),
    startTerm: {
      get() {
        return this.conditions.startTerm
      },
      set(value) {
        const startTerm = yearMonthToDate(value)
        let endTerm = yearMonthToDate(this.endTerm)
        const diffMonths = differenceInMonths(endTerm, startTerm)

        // 検索期間が24ヶ月以上の場合
        if (diffMonths >= 24) {
          endTerm = addMonths(startTerm, 23)
        }

        // 検索期間の未来・過去日の関係が逆転している場合
        if (diffMonths < 0) {
          endTerm = startTerm
        }

        this.$store.commit('analysis/aggregate/update', {
          startTerm: format(startTerm, 'YYYY-MM'),
          endTerm: format(endTerm, 'YYYY-MM'),
        })
      },
    },
    endTerm: {
      get() {
        return this.conditions.endTerm
      },
      set(value) {
        let startTerm = yearMonthToDate(this.startTerm)
        const endTerm = yearMonthToDate(value)
        const diffMonths = differenceInMonths(endTerm, startTerm)

        // 検索期間が24ヶ月以上の場合
        if (diffMonths >= 24) {
          startTerm = subMonths(endTerm, 23)
        }

        // 検索期間の未来・過去日の関係が逆転している場合
        if (diffMonths < 0) {
          startTerm = endTerm
        }

        this.$store.commit('analysis/aggregate/update', {
          startTerm: format(startTerm, 'YYYY-MM'),
          endTerm: format(endTerm, 'YYYY-MM'),
        })
      },
    },
    statusType: {
      get() {
        return !!this.conditions.statusType
      },
      set(value) {
        this.$store.commit('analysis/aggregate/update', { statusType: value ? 1 : 0 })
      },
    },
    timeZones() {
      return { 0: 'すべての予約', 1: 'ランチ', 2: 'ディナー' }
    },
    types() {
      return new Map([
        [0, 'すべて'],
        [1, '電話予約'],
        [2, 'ネット予約'],
        ['-5', 'ネット予約（レスラク経由）'],
        [3, 'ウォークイン'],
      ])
    },
    changed() {
      return isEqual(this.beforeConditions, this.conditions)
    },
  },
  watch: {
    $route() {
      this.beforeConditions = { ...this.conditions }
    },
  },
  mounted() {
    this.$store.dispatch('analysis/aggregate/start', { defaultUnit: this.defaultUnit })
    this.beforeConditions = { ...this.conditions }
  },
}
</script>

<style lang="postcss" scoped>
.aggregate {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  max-width: 728px;
  padding-bottom: 28px;

  & > * {
    margin-right: 8px;
  }

  .status {
    gap: 4px;
    margin-right: 14px;
  }

  .time-zone {
    width: 172px;
  }

  .route {
    width: 312px;
  }

  .terms {
    align-items: center;
    display: grid;
    gap: 8px;
    grid-auto-flow: column;

    >>> div.start,
    >>> div.end {
      width: 140px;
    }
  }

  .search {
    width: 88px;
  }

  .remarks {
    color: var(--dark-gray);
    font-size: 10px;
    line-height: 2;
    margin-right: 0;
    text-align: right;
  }

  &.break {
    padding-bottom: 8px;

    .search {
      margin-right: 0;
    }
    .remarks {
      margin-left: auto;
    }
  }
}
</style>
